if (!customElements.get('announcement-claims-rotator')) {
  customElements.define(
    'announcement-claims-rotator',
    class AnnouncementClaimsRotator extends HTMLElement {
      constructor() {
        super();
        this.claims = Array.from(this.querySelectorAll('[data-claim]'));
        this.toggleButton = this.querySelector('[data-toggle]');
        this.currentIndex = this.claims.findIndex((claim) => claim.classList.contains('is-active'));
        this.currentIndex = this.currentIndex < 0 ? 0 : this.currentIndex;
        this.interval = Number(this.dataset.interval) || 4000;
        this.transitionDuration = Number(this.dataset.transition) || 500;
        this.pauseOnHover = this.dataset.pauseOnHover === 'true';
        this.userPaused = this.dataset.autoplay !== 'true';
        this.hoverPaused = false;
        this.rotationTimer = null;
        this.isAnimating = false;

        this.handleToggleClick = this.handleToggleClick.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
      }

      connectedCallback() {
        if (this.claims.length <= 1) {
          if (this.toggleButton) this.toggleButton.setAttribute('hidden', '');
          return;
        }

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.userPaused = true;
        }

        this.claims.forEach((claim, index) => {
          const isActive = index === this.currentIndex;
          claim.classList.toggle('is-active', isActive);
          claim.classList.remove('is-leaving', 'is-next');
          claim.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        if (this.toggleButton) {
          this.toggleButton.addEventListener('click', this.handleToggleClick);
          this.updateToggleState();
        }

        if (this.pauseOnHover) {
          this.addEventListener('mouseenter', this.handleMouseEnter);
          this.addEventListener('mouseleave', this.handleMouseLeave);
        }

        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        document.addEventListener('shopify:block:select', this.handleBlockSelect);

        this.startRotation();
      }

      disconnectedCallback() {
        this.stopRotation();
        if (this.toggleButton) {
          this.toggleButton.removeEventListener('click', this.handleToggleClick);
        }
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      }

      canRotate() {
        return !this.userPaused && !this.hoverPaused && !document.hidden;
      }

      startRotation() {
        this.stopRotation();
        if (!this.canRotate()) return;
        this.rotationTimer = window.setInterval(() => this.goToNextClaim(), this.interval);
      }

      stopRotation() {
        if (!this.rotationTimer) return;
        window.clearInterval(this.rotationTimer);
        this.rotationTimer = null;
      }

      handleMouseEnter() {
        this.hoverPaused = true;
        this.stopRotation();
      }

      handleMouseLeave() {
        this.hoverPaused = false;
        this.startRotation();
      }

      handleVisibilityChange() {
        this.startRotation();
      }

      handleToggleClick() {
        this.userPaused = !this.userPaused;
        this.updateToggleState();
        this.startRotation();
      }

      updateToggleState() {
        if (!this.toggleButton) return;
        const isRunning = !this.userPaused;
        this.toggleButton.dataset.state = isRunning ? 'running' : 'paused';
        this.toggleButton.setAttribute(
          'aria-label',
          isRunning ? this.toggleButton.dataset.labelPause : this.toggleButton.dataset.labelPlay
        );
      }

      handleBlockSelect(event) {
        if (event.detail.sectionId !== this.dataset.sectionId) return;
        const blockId = event.detail.blockId;
        const targetIndex = this.claims.findIndex((claim) => claim.dataset.blockId === blockId);
        if (targetIndex < 0) return;

        this.userPaused = true;
        this.stopRotation();
        this.showClaim(targetIndex, false);
        this.updateToggleState();
      }

      goToNextClaim() {
        if (this.isAnimating || this.claims.length <= 1) return;
        const nextIndex = (this.currentIndex + 1) % this.claims.length;
        this.showClaim(nextIndex);
      }

      showClaim(targetIndex, animate = true) {
        if (targetIndex === this.currentIndex || targetIndex < 0 || targetIndex >= this.claims.length) return;

        if (!animate) {
          this.claims.forEach((claim, index) => {
            const isActive = index === targetIndex;
            claim.classList.toggle('is-active', isActive);
            claim.classList.remove('is-leaving', 'is-next');
            claim.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          });
          this.currentIndex = targetIndex;
          return;
        }

        this.isAnimating = true;
        const currentClaim = this.claims[this.currentIndex];
        const nextClaim = this.claims[targetIndex];

        nextClaim.classList.add('is-next');
        nextClaim.setAttribute('aria-hidden', 'false');

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            currentClaim.classList.add('is-leaving');
            nextClaim.classList.add('is-active');
            nextClaim.classList.remove('is-next');
          });
        });

        window.setTimeout(() => {
          currentClaim.classList.remove('is-active', 'is-leaving');
          currentClaim.setAttribute('aria-hidden', 'true');
          this.currentIndex = targetIndex;
          this.isAnimating = false;
        }, this.transitionDuration);
      }
    }
  );
}
